import { css } from 'lit';
import { wait } from '../../../@helpers/js/wait.js';
import { CzContentloader } from '@cz/webcomponents';

export default class CzVergelijkerloader extends CzContentloader {

	static styles = [
		CzContentloader.styles,
		css`
			@layer component {
				:host(:empty) {
					aspect-ratio: 1/.325;
				}
			}
		`
	];

	static properties = {
		date: {},
		productset: {},
		producttype: {},
		collectivity: {}
	};

	#svglocation = '../../images/svg.svg';
	#meldingOphalenGegevens = 'Geen gegevens gevonden';
	#apimKeySalesfunnel = false;

	#sidepanel = false;
	#loaded = false;
	#loadOnSidepanelOpen = false;

	async startLoading() {
		if (this._isLoading) {
			return;
		}
		while (!this.#loaded && ((this.#loadOnSidepanelOpen && this.#sidepanel?.hasAttribute('open')) || !this.#loadOnSidepanelOpen)) {
			this._isLoading = true;
			this.loadContent().then(() => {
				window.processModulesInsideElement(this);
				this._isLoading = !this.#loaded;
			});
			await wait(15000);
		}
	}
	
	sideOverlayObserve() {
		if (!this.#sidepanel) {
			return;
		}

		const config = {
			attributeFilter: ['open'],
		};

		const callback = async (mutationList) => {
			for (const mutation of mutationList) {
				if (mutation.type === "attributes" && mutation.attributeName === 'open') {
					if (this.#sidepanel?.hasAttribute('open') && !this.#loaded) {
						this.startLoading();
					} else {
						this._isLoading = false;
					}
				}
			}
		};

		const observer = new MutationObserver(callback);
		observer.observe(this.#sidepanel, config);

		if (this.#sidepanel?.hasAttribute('open') && !this.#loaded) {
			this.startLoading();
		}
	}

	updated(changedProperties) {
		if (changedProperties?.has('date') ||
			changedProperties?.has('productset') ||
			changedProperties?.has('producttype') ||
			changedProperties?.has('collectivity')) {
				this.innerHTML = '';
				this.#loaded = false;
				if (this.#loadOnSidepanelOpen && this.#sidepanel) {
					if (this.#sidepanel.hasAttribute('open')) {
						this.startLoading();
					}
				} else {
					this.startLoading();
				}
			}
	}

	callBackend = async (url) => {
		try {
			const requestUrl = url;
			const postData = {
				"collectiviteitsnummer": this.collectivity,
				"referentie_datum": this.date ?? "2024-12-1",
				"productset": this.productset,
				"soort_verzekering": this.producttype
			}
		
			const controller  = window.AbortController ? new AbortController() : {};
			const signal = controller.signal || {};
			const headers = {
				"Content-Type": "application/json; charset=utf-8",
			};
			if (this.#apimKeySalesfunnel) {
				headers['Ocp-Apim-Subscription-Key'] = this.#apimKeySalesfunnel;
			}
			const fetchPromise = fetch(requestUrl, {
				signal,
				method: "POST",
				body: JSON.stringify(postData),
				headers
			  });
			const response = await fetchPromise;
			if (!response.ok) {
				const err = await response.json();
				throw new Error(err.Message);
			} else {
				let result = await response.json();
				this.#loaded = true;
				return this.#bakeHtmlFromJsonResult(result);
			}
		} catch (error) {
			return this.#errorNotification(error.message || this.#meldingOphalenGegevens);
		}
	}

	#errorNotification(message) {
		return `<div class="c-notification c-notification--error">
					<span>${message}</span>
				</div>`;
	}

	#bakeHtmlFromJsonResult(result) {
		let numberFormatter = Intl.NumberFormat( document.firstElementChild?.lang || navigator.language || "nl-NL" );
        let returnValue = '';
        if (result) {
			returnValue = `<div class="c-insurance-compare" data-module="CompareInsuranceTable" data-options='{"searchPlaceholder": "${result?.VergoedingenvergelijkerLabels?.WatermerkVergoedingSelecteren || ''}", "searchText": "${result?.VergoedingenvergelijkerLabels?.VergoedingeNietGevonden || `Geen vergoedingen gevonden met deze omschrijving`}"}'>
			<div class="c-insurance-compare__table">
				<h2 class="c-heading"><span>${result?.VergoedingenvergelijkerLabels?.TitelVergoedingenvergelijker || ''}</span></h2>
				<table class="c-table">
					<thead>
						<tr class="c-insurance">
							<td scope="col" class="c-table__upperleft-cel">${result?.VergoedingenvergelijkerLabels?.TitelVergoeding || 'Vergoedingen'}</td>
							${result?.Vergoedingen?.[0]?.AanvullendeZorgVerzekeringen?.map(av => `<th scope="col" class="c-input__label">
								<span class="c-input__label__introtext">${result?.VergoedingenvergelijkerLabels?.TitelAanvullendeVerzekering || 'Aanvullend'}</span>
								<span class="c-input__label__text label--full-color">${av?.Naam}</span>
								<div class="c-input__label__price">
									<span class="c-input__label__price__amount">
										<span class="c-amount c-ownrisk__noerror" data-module="SuperCents" data-options='{"addEuroSign": true}'>${av?.Prijs ? numberFormatter.format(av.Prijs) : numberFormatter.format(0.0)}</span>
									</span>
									<span class="c-input__label__price__text">${result.VergoedingenvergelijkerLabels?.PerTermijn|| '/mnd'}</span>
								</div>
							</th>`).join("")}
						</tr>
					</thead>
					<tbody>
						${result?.Vergoedingen?.map((vergoeding, index) => {
							return `<tr data-tablerow="row-${index}" class="${vergoeding?.Tonen ? '' : 't-hidden c-table-row-highlight'}">
								<th scope="row" data-module="ToggleButtonListener" data-options="{&quot;triggerName&quot;: &quot;triggername_${index}${index}&quot;, &quot;className&quot;: &quot;c-toggle-detail-on&quot;}">
									<button type="button" data-insurance-compare-hider class="c-link c-insurance-compare__removal" data-datalayer-click="{&quot;category&quot;: &quot;button click&quot;, &quot;action&quot;: &quot;verwijder&quot;, &quot;label&quot;: &quot;${vergoeding?.CategorieZorgsoort}&quot;}">${result?.VergoedingenvergelijkerLabels?.Verwijder}</button>
									<div class="c-insurance-compare__name">${vergoeding?.CategorieZorgsoort}</div>
									${vergoeding?.TonenDetails ? `<div class="c-toggle-button-link">
										<button type="button" role="switch" aria-checked="false" data-module="ToggleButton" data-options="{&quot;triggerName&quot;: &quot;triggername_vergoeding${index}&quot;}" data-datalayer-click="{&quot;category&quot;: &quot;button click&quot;, &quot;action&quot;: &quot;Verberg-Toon details&quot;, &quot;label&quot;: &quot;Tandarts en mondhygiënist&quot;}">
											<span class="c-toggle-button-link__label">details</span>
											<span class="c-toggle-button-link__icon-holder">
												<svg focusable="false" aria-hidden="true" class="c-icon c-icon--chevron"><use xlink:href="${this.#svglocation}#chevron"></use></svg>
											</span>
										</button>
									</div>`: ''}
								</th>
								${vergoeding?.AanvullendeZorgVerzekeringen?.map(av => `<td>
									<span class="c-insurance__detail">${av?.VergoedingOmschrijving}</span>
									<div class="c-insurance__detail--extended" data-module="ToggleButtonListener" data-options='{"triggerName": "triggername_vergoeding${index}", "className": "c-insurance__detail--expanded"}'>
										<div class="c-insurance__detail--extended--inner">${av?.VergoedingDetails}</div>
									</div>
								</td>`).join("")}
							</tr>`
						}).join("")}
					</tbody>
				</table>
			</div>
		
			<section class="c-revealer-collection c-revealer-collection__compact">
				<h2 class="c-heading">${result?.VergoedingenvergelijkerLabels?.TitelVergoedingenvergelijker || 'Vergelijk onze aanvullende verzekeringen'}</h2>
		
				${result?.Vergoedingen?.map((vergoeding, index) => {
					return `<article class="c-revealer ${vergoeding.Tonen ? '' : 't-hidden c-table-row-highlight t-background-color-transition'}" data-module="Revealer" data-revealerrow="row-${index}" data-open="false">
						<h3 id="l${index}" data-revealer-title class="like-h4 c-revealer__heading">
							<span class="c-revealer__title__text">
								${vergoeding?.CategorieZorgsoort}
							</span>
							<svg focusable="false" aria-hidden="true" class="c-icon c-icon--chevron c-icon--rotate-right"><use xlink:href="${this.#svglocation}#chevron"></use></svg>
						</h3>
		
						<section class="c-revealer__content" id="c${index}" data-revealer-content aria-labelledby="l${index}">
							<div data-inner>
								${vergoeding.TonenDetails ? `<label class="c-toggle-button">
									<span class="c-toggle-button__label">${result?.VergoedingenvergelijkerLabels?.ToonDetails}</span>
									<div class="c-toggle-button__action">
										<input type="checkbox" data-module="ToggleButton" role="switch" data-options='{"triggerName": "triggername_compactvergoeding${index}"}'>
										<span class="c-toggle-button__on" aria-hidden="true">${result?.VergoedingenvergelijkerLabels?.ToondetailOpties?.[0]?.Value || 'aan'}</span>
										<span class="c-toggle-button__off" aria-hidden="true">${result?.VergoedingenvergelijkerLabels?.ToondetailOpties?.[1]?.Value || 'uit'}</span>
									</div>
								</label>`: ''}
								<table class="c-table"><caption class="t-visuallyhidden">${vergoeding?.CategorieZorgsoort}</caption>
									<tbody><tr>
										<th scope="col">${result?.VergoedingenvergelijkerLabels?.TitelAanvullendeVerzekering}</th>
										<th scope="col">${result?.VergoedingenvergelijkerLabels?.TitelVergoeding}</th>
									</tr>
									${vergoeding?.AanvullendeZorgVerzekeringen?.map(av => `
										<tr>
											<td>${av.Naam}</td>
											<td>
												<span>${av.VergoedingOmschrijving}</span>
												<div class="t-hidden" data-module="ToggleButtonListener" data-options="{&quot;triggerName&quot;: &quot;triggername_compactvergoeding${index}&quot;}">${av.VergoedingDetails === '-' ? '' : av.VergoedingDetails}</div>
											</td>
										</tr>`).join("")}
								</tbody></table>
							</div>
						</section>
					</article>
					<div class="c-insurance-compare__removal__wrapper">
						<button type="button" data-insurance-compare-hider="" class="c-insurance-compare__removal" data-datalayer-click="{&quot;category&quot;: &quot;button click&quot;, &quot;action&quot;: &quot;verwijder&quot;, &quot;label&quot;: &quot;Fysiotherapie bij een blessure of tijdelijke klachten&quot;}">
							<span class="t-visuallyhidden">${result?.VergoedingenvergelijkerLabels?.Verwijder} ${vergoeding?.CategorieZorgsoort}</span>
							<svg focusable="false" aria-hidden="true" class="c-icon c-icon--crossout"><use xlink:href="${this.#svglocation}#crossout"></use></svg>
						</button>
					</div>`
				}).join("")}
			</section>
		
			<p class="t-hidden" data-insurance-compare-none="">${result?.VergoedingenvergelijkerLabels?.SelecteerVergoeding}</p>
		
			<section class="c-insurance-compare__select c-form" aria-labelledby="label_fd338bfcd821">
				<div data-inner="" class="c-insurance-compare__select__inner">
					<h2 class="t-visuallyhidden" id="label_fd338bfcd821">${result?.VergoedingenvergelijkerLabels?.TitelVergoedingToevoegen}</h2>
					<div class="c-insurance-compare__combobox" data-module="ComboboxAutocomplete">
						<label class="c-input__label" for="select_90f6cf6ebf1d">${result?.VergoedingenvergelijkerLabels?.TitelVergoedingToevoegen}</label>
		
						<div class="c-search-form__querybox c-insurance-compare__combobox__group" data-focus-container="">
							<input type="text" id="select_90f6cf6ebf1d" aria-controls="select_90f6cf6ebf1d-listbox" class="c-input__input" autocomplete=off role="combobox" aria-autocomplete="list" aria-expanded="false">
							<button type="button" tabindex="-1" aria-label="${result?.VergoedingenvergelijkerLabels?.TitelVergoeding || 'Vergoedingen'}" aria-expanded="false" aria-controls="cb1-listbox">
								<svg focusable="false" aria-hidden="true" class="c-icon c-icon--chevron"><use xlink:href="${this.#svglocation}#chevron"></use></svg>
							</button>
							<button type="button" data-confirm-selection class="c-button c-button--tertiary c-search-form__querybox__button" data-confirm-datalayer-click="{&quot;category&quot;: &quot;button click&quot;, &quot;action&quot;: &quot;Voeg vergoeding toe&quot;, &quot;label&quot;: &quot;label&quot;}">
								<span><svg focusable="false" aria-hidden="true" class="c-icon c-icon--plus"><use xlink:href="${this.#svglocation}#plus"></use></svg></span>
								<span class="c-search-form__querybox__button__text">${result?.VergoedingenvergelijkerLabels?.ButtonVergoedingToevoegen}</span>
							</button>
							<ul id="select_90f6cf6ebf1d-listbox" class="c-insurance-compare__combobox__list" role="listbox">
							${result?.Vergoedingen?.map((vergoeding, index) => {
								return `<li role="option" id="row-${index}" ${vergoeding?.Tonen ? `disabled class="t-hidden"` : ``}>${vergoeding?.CategorieZorgsoort}</li>`;
							}).join("")}
							</ul>
						</div>
					</div>
				</div>
			</section>
		</div>`
        }
        return returnValue;
    }


	constructor() {
		super();
		this.#svglocation = this.dataset?.svgUrl || this.#svglocation;
		this.#meldingOphalenGegevens = this.dataset?.meldingOphalenGegevens;
		this.#apimKeySalesfunnel = this.getAttribute('salesfunnel_apim_key');
		this.#loadOnSidepanelOpen = this.getAttribute('load-on-sidepanel-open');
		this.#sidepanel = this.closest('cz-sidepanel');
		if (this.#loadOnSidepanelOpen && this.#sidepanel) {
			this.sideOverlayObserve();
		}
	}
}

customElements.define('cz-vergelijkerloader', CzVergelijkerloader);